import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Copy, Check, MessageCircle, Mail, Smartphone, Send } from 'lucide-react';
import { toast } from 'sonner';

export const TaskLinkDialog = ({ task, url, onClose, onSendEmail, defaultEmail = '', justCreated = false }) => {
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const shareText = task ? `Sila Meži darba uzdevums: ${task.title}${task.project_name ? ` (${task.project_name})` : ''}\n${url}` : url;

  useEffect(() => {
    setEmail(defaultEmail || '');
    setMessage('');
    setSentTo('');
    setCopied(false);
  }, [task?.id, defaultEmail]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Saite nokopēta');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Neizdevās nokopēt. Iezīmējiet un kopējiet manuāli.');
    }
  };

  const sendEmail = async () => {
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Ievadiet derīgu e-pasta adresi');
      return;
    }
    setSending(true);
    try {
      const result = await onSendEmail(email, message || null);
      setSentTo(email);
      toast.success(result?.message || `Saite nosūtīta uz ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās nosūtīt e-pastu');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!task} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="task-link-dialog">
        <DialogHeader>
          <DialogTitle data-testid="task-link-dialog-title">{justCreated ? 'Uzdevums izveidots — nosūtiet saiti darbiniekam' : 'Darba uzdevuma saite'}</DialogTitle>
          <DialogDescription data-testid="task-link-dialog-desc">
            {task?.title} · {task?.employee_name || 'darbinieks'}. Saite prasa pieslēgšanos; to var atvērt tikai piešķirtais darbinieks.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-[#2E2E2E]/60">Tiešā saite</Label>
            <div className="flex gap-2">
              <Input readOnly value={url} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid="task-link-url" />
              <Button onClick={copy} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="task-link-copy">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button asChild variant="outline" data-testid="task-link-whatsapp">
                <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</a>
              </Button>
              <Button asChild variant="outline" data-testid="task-link-sms">
                <a href={`sms:?body=${encodeURIComponent(shareText)}`}><Smartphone className="w-4 h-4 mr-1" /> SMS</a>
              </Button>
              <Button asChild variant="outline" data-testid="task-link-email">
                <a href={`mailto:${encodeURIComponent(email || '')}?subject=${encodeURIComponent('Darba uzdevums')}&body=${encodeURIComponent(shareText)}`}><Mail className="w-4 h-4 mr-1" /> E-pasta progr.</a>
              </Button>
            </div>
          </div>

          {onSendEmail && (
            <div className="rounded-xl border border-[#1F3D2B]/15 bg-[#f6f9f6] p-4 space-y-3" data-testid="task-link-send-section">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-[#1F3D2B]" />
                <p className="font-semibold text-[#1F3D2B] text-sm">Nosūtīt darbiniekam e-pastā no portāla</p>
              </div>
              <p className="text-xs text-[#2E2E2E]/60">E-pastā tiks iekļauts uzdevuma nosaukums, objekts, apjoms, termiņi, norādījumi un poga “Atvērt darba uzdevumu”.</p>
              <div className="space-y-1">
                <Label>Darbinieka e-pasts</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vards@epasts.lv" data-testid="task-link-send-email" />
              </div>
              <div className="space-y-1">
                <Label>Papildu ziņa (neobligāti)</Label>
                <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Piem.: lūdzu sākt pirmdien no rīta" data-testid="task-link-send-message" />
              </div>
              <div className="flex items-center justify-between gap-2">
                {sentTo ? <span className="text-xs text-green-700 font-medium" data-testid="task-link-sent-info">Nosūtīts uz {sentTo}</span> : <span />}
                <Button onClick={sendEmail} disabled={sending} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="task-link-send-submit">
                  <Send className="w-4 h-4 mr-2" /> {sending ? 'Sūta...' : sentTo ? 'Nosūtīt vēlreiz' : 'Nosūtīt darbiniekam'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskLinkDialog;
